import React from 'react';
import { useNavigate, useLocation } from 'react-router';

export const parseAsNumber = (value: string) => +value;
export const parseAsString = <T extends string = string>(value: string): T => value as T;

export const useQuery = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const urlSearchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const setQuery = (parameters: Record<string, string>, options?: { replace?: boolean }) => {
    const keys = Object.keys(parameters);
    const newUrlSearchParams = new URLSearchParams();
    keys.forEach((key) => {
      parameters[key] && newUrlSearchParams.append(key, parameters[key]);
    });

    navigate({
      pathname: location.pathname,
      search: newUrlSearchParams.toString(),
      hash: location.hash,
    }, {
      replace: options?.replace ?? false,
    });
  };

  const updateQuery = (parameters: Record<string, string | null>, options?: { replace?: boolean }) => {
    const newUrlSearchParams = new URLSearchParams(location.search);
    
    Object.keys(parameters).forEach((key) => {
      const value = parameters[key];
      if (value === null) {
        newUrlSearchParams.delete(key);
      } else if (value) {
        newUrlSearchParams.set(key, value);
      }
    });

    navigate({
      pathname: location.pathname,
      search: newUrlSearchParams.toString(),
      hash: location.hash,
    }, {
      replace: options?.replace ?? false,
    });
  };

  const getQueryParam = <T>(key: string, parse: (value: string) => T): T | undefined => {
    const value = urlSearchParams.get(key);
    return value ? parse(value) : undefined;
  };

  const getQueryParams = <T>(key: string, parse: (value: string) => T): T[] | undefined => {
    const values = urlSearchParams.get(key)?.split(',') ?? [];
    return values.length > 0 ? values.map((value) => parse(value)) : undefined;
  };

  return {
    urlSearchParams,
    setQuery,
    updateQuery,
    getQueryParam,
    getQueryParams,
  };
};
